import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { EditStatusType, StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import { ContractObjectDefaults } from './contract-object.defaults';
import { CommRateType, PartyNodeType } from './contract-object.types';
import { SyncContractBroker, SyncContractBrokerAffiliate } from './contract.actions';
import { ContractState } from './contract.state';
import { AddContractPartyItem, RemoveContractPartyItem, UpdateLine } from './line.actions';
import { LineState } from './line.state';
import {
    AddParty,
    ClearParty,
    CloneParty,
    DeleteParty,
    RestoreParty,
    SetTPBillToEntity,
    UpdateParty,
    UpdatePartyAgreement
} from './party.actions';
import { RiskGroupState } from './risk-group.state';
import { TeamSetService } from './team-set.service';

export interface PartyStateModel {
    party: any;
    currentID: string;
    tpBillToEntity: any;
}
// add currentID?

@State<PartyStateModel>({
    name: 'partyState',
    defaults: {
        party: {},
        currentID: null,
        tpBillToEntity: {}
    }
})
@Injectable()
export class PartyState {
    constructor(private helper: StateHelperService, private store: Store, private teamSetService: TeamSetService) { }

    //******* SELECTORS *********

    @Selector()
    static fullState(state: PartyStateModel) {
        //temporary selector for debugging
        return state.party;
    }

    @Selector()
    static brokerParties(state: PartyStateModel) {
        //return all brokerPartys in Code table format
        let result: any = [];
        for (var i in state.party) {
            if (state.party[i].nodeType == PartyNodeType.Contract_Broker) {
                result.push(state.party[i]);
            }
        }
        return result;
    }

    @Selector()
    static brokerPartyCount(state: PartyStateModel) {
        //return article count of a particular type
        let count = 0;
        for (var i in state.party) {
            if (state.party[i].nodeType == PartyNodeType.Contract_Broker) {
                count++;
            }
        }
        return count;
    }

    @Selector()
    static principalBrokerParty(state: PartyStateModel) {
        //returns principal supplier
        let prinBroker: any = {};
        for (var i in state.party) {
            if (
                state.party[i].nodeType == PartyNodeType.Contract_Broker &&
                state.party[i].Principal_Ind === 'Y' &&
                state.party[i].Edit_Status != EditStatusType.Delete
            ) {
                prinBroker = { ...state.party[i] };
            }
        }
        return prinBroker ? prinBroker : {};
    }

    @Selector()
    static supplierParties(state: PartyStateModel) {
        //return all brokerPartys in Code table format
        let result: any = [];
        for (var i in state.party) {
            if (state.party[i].nodeType == PartyNodeType.Contract_Supplier) {
                result.push(state.party[i]);
            }
        }
        return result;
    }

    @Selector()
    static supplierPartyCount(state: PartyStateModel) {
        //return article count of a particular type
        let count = 0;
        for (var i in state.party) {
            if (state.party[i].nodeType == PartyNodeType.Contract_Supplier) {
                count++;
            }
        }
        return count;
    }

    @Selector()
    static principalSupplierParty(state: PartyStateModel) {
        //returns principal supplier
        let prinSupplier: any = {};
        for (var i in state.party) {
            if (
                state.party[i].nodeType == PartyNodeType.Contract_Supplier &&
                state.party[i].Principal_Ind === 'Y' &&
                state.party[i].Edit_Status != EditStatusType.Delete
            ) {
                prinSupplier = { ...state.party[i] };
            }
        }
        return prinSupplier ? prinSupplier : {};
    }

    @Selector()
    static producerParties(state: PartyStateModel) {
        //return all brokerPartys in Code table format
        let result: any = [];
        for (var i in state.party) {
            if (state.party[i].nodeType == PartyNodeType.Contract_Producer) {
                result.push(state.party[i]);
            }
        }
        return result;
    }

    @Selector()
    static producerPartyCount(state: PartyStateModel) {
        //return article count of a particular type
        let count = 0;
        for (var i in state.party) {
            if (state.party[i].nodeType == PartyNodeType.Contract_Producer) {
                count++;
            }
        }
        return count;
    }

    @Selector()
    static principalProducerParty(state: PartyStateModel) {
        //returns principal supplier
        let prinProducer: any = {};
        for (var i in state.party) {
            if (
                state.party[i].nodeType == PartyNodeType.Contract_Producer &&
                state.party[i].Principal_Ind === 'Y' &&
                state.party[i].Edit_Status != EditStatusType.Delete
            ) {
                prinProducer = { ...state.party[i] };
            }
        }
        return prinProducer ? prinProducer : {};
    }

    @Selector()
    static financeCoParties(state: PartyStateModel) {
        //return all brokerPartys in Code table format
        let result: any = [];
        for (var i in state.party) {
            if (state.party[i].nodeType == PartyNodeType.Contract_FinanceCo) {
                result.push(state.party[i]);
            }
        }
        return result;
    }

    @Selector()
    static financeCoPartyCount(state: PartyStateModel) {
        //return article count of a particular type
        let count = 0;
        for (var i in state.party) {
            if (state.party[i].nodeType == PartyNodeType.Contract_FinanceCo) {
                count++;
            }
        }
        return count;
    }

    @Selector()
    static partiesWithoutAgmt(state: PartyStateModel) {
        let result: any = [];
        for (var i in state.party) {
            if (
                state.party[i].nodeType == PartyNodeType.Contract_Broker ||
                state.party[i].nodeType == PartyNodeType.Contract_Supplier
            ) {
                if (state.party[i].Agreement == null || state.party[i].Agreement == '') {
                    result.push(state.party[i]);
                }
            }
        }
        return result;
    }

    //******  ACTIONS  ******

    @Action(UpdateParty)
    updateParty(sc: StateContext<PartyStateModel>, action: UpdateParty) {
        const state = sc.getState();
        let addNew = false;
        let lineChange = false;
        let partyID = action.party.Party_ID;
        let updateParty: any = { ...action.party }; //need DEEP copy?
        this.helper.setItemChanged(updateParty, state.party[partyID].Edit_Status);
        const currentParty = state.party[partyID];
        const lines = this.store.selectSnapshot(LineState.lines);
        let lineID = currentParty.Line_ID;
        let newLineID = null;
        let commRatesUpdated = false;

        if (
            currentParty.nodeType == PartyNodeType.Contract_Broker ||
            currentParty.nodeType == PartyNodeType.Contract_Supplier
        ) {
            if (
                this.helper.stateValueChanged(updateParty.State, currentParty.State) ||
                this.helper.stateValueChanged(updateParty.Program, currentParty.Program) ||
                this.helper.stateValueChanged(updateParty.Line_Code, currentParty.Line_Code)
            ) {
                updateParty.agreementValidated = 'N';
            }
        }

        if (currentParty.Manual_Comm_Ind == 'Y') {
            if (this.helper.stateValueChanged(updateParty.New_Comm_Rate, currentParty.New_Comm_Rate)) {
                commRatesUpdated = true;
                this.updateOtherUnsetCommRates(updateParty.New_Comm_Rate, CommRateType.New_Comm_Rate, updateParty);
            }

            if (
                !commRatesUpdated &&
                this.helper.stateValueChanged(updateParty.Renewal_Comm_Rate, currentParty.Renewal_Comm_Rate)
            ) {
                commRatesUpdated = true;
                this.updateOtherUnsetCommRates(updateParty.Renewal_Comm_Rate, CommRateType.Renewal_Comm_Rate, updateParty);
            }

            if (
                !commRatesUpdated &&
                this.helper.stateValueChanged(updateParty.Rewrite_Comm_Rate, currentParty.Rewrite_Comm_Rate)
            ) {
                commRatesUpdated = true;
                this.updateOtherUnsetCommRates(updateParty.Rewrite_Comm_Rate, CommRateType.Rewrite_Comm_Rate, updateParty);
            }

            if (
                !commRatesUpdated &&
                this.helper.stateValueChanged(updateParty.Endorse_Comm_Rate, currentParty.Endorse_Comm_Rate)
            ) {
                commRatesUpdated = true;
                this.updateOtherUnsetCommRates(updateParty.Endorse_Comm_Rate, CommRateType.Endorse_Comm_Rate, updateParty);
            }

            if (
                !commRatesUpdated &&
                this.helper.stateValueChanged(updateParty.Renewal_Endorse_Comm_Rate, currentParty.Renewal_Endorse_Comm_Rate)
            ) {
                commRatesUpdated = true;
                this.updateOtherUnsetCommRates(
                    updateParty.Renewal_Endorse_Comm_Rate,
                    CommRateType.Renewal_Endorse_Comm_Rate,
                    updateParty
                );
            }

            if (commRatesUpdated) {
                let rateType = this.store.selectSnapshot(ContractState.contract).Comm_Rate_Type;
                switch (rateType) {
                    case 'RENEWAL':
                        updateParty.Comm_Rate = updateParty.Renewal_Comm_Rate;
                        break;
                    case 'REWRITE':
                        updateParty.Comm_Rate = updateParty.Rewrite_Comm_Rate;
                        break;
                    case 'ENDORSE':
                        updateParty.Comm_Rate = updateParty.Endorse_Comm_Rate;
                        break;
                    case 'RENEWAL_ENDORSE':
                        updateParty.Comm_Rate = updateParty.Renewal_Endorse_Comm_Rate;
                        break;
                    default:
                        updateParty.Comm_Rate = updateParty.New_Comm_Rate;
                        break;
                }
            }
        }

        if (currentParty.nodeType == PartyNodeType.Contract_Broker && currentParty.Principal_Ind == 'Y') {
            if (
                updateParty.Affiliate &&
                currentParty.Affiliate &&
                updateParty.Affiliate.Entity_ID != currentParty.Affiliate.Entity_ID
            ) {
                sc.dispatch([new SyncContractBrokerAffiliate(updateParty.Affiliate)]);
            }
        }

        if (updateParty.Edit_Status != EditStatusType.Add && updateParty.Line_Code != currentParty.Line_Code) {
            addNew = true;
        }

        if (updateParty.Edit_Status == EditStatusType.Add && updateParty.Line_Code != currentParty.Line_Code) {
            for (var i in lines) {
                if (lines[i].Line_Code == updateParty.Line_Code) {
                    newLineID = lines[i].Line_ID;
                    updateParty.Line_ID = newLineID;
                    delete updateParty.Agreement;
                    lineChange = true;
                    break;
                }
            }
        }

        if (!addNew)
            sc.patchState({
                party: {
                    ...state.party,
                    [partyID]: {
                        ...state.party[partyID],
                        ...updateParty
                    }
                }
            });

        //If Party record's Principal_Ind is changed to "Y", Set to "N" for all other Party Records of the same type.

        let lineIDs: any = [];
        if (updateParty.Principal_Ind != currentParty.Principal_Ind && updateParty.Principal_Ind == 'Y') {
            let nodeType = currentParty.nodeType;
            let updateStateParty = cloneDeep(state.party);
            for (var i in updateStateParty) {
                if (updateStateParty[i].nodeType == nodeType) {
                    this.helper.setItemChanged(updateStateParty[i], updateStateParty[i].Edit_Status);

                    if (lineIDs.indexOf(updateStateParty[i].Line_ID) == -1) {
                        lineIDs.push(updateStateParty[i].Line_ID);
                    }

                    if (updateStateParty[i].Party_ID == partyID) {
                        updateStateParty[i].Principal_Ind = 'Y';
                    } else {
                        updateStateParty[i].Principal_Ind = 'N';
                    }
                }
            }

            sc.patchState({ party: updateStateParty });

            if (nodeType == PartyNodeType.Contract_Broker) {
                sc.dispatch([new SyncContractBroker(updateParty.Broker)]);
            }
        }

        //If Broker record's Net_Broker_Ind is changed to "Y", Set to "N" for all other Brokers with different Entity_ID.
        if (currentParty.nodeType == PartyNodeType.Contract_Broker) {
            let updateStateParty = cloneDeep(state.party);
            if (updateParty.Net_Broker_Ind != currentParty.Net_Broker_Ind) {
                for (var i in updateStateParty) {
                    if (updateStateParty[i].nodeType == PartyNodeType.Contract_Broker) {
                        this.helper.setItemChanged(updateStateParty[i], updateStateParty[i].Edit_Status);

                        if (lineIDs.indexOf(updateStateParty[i].Line_ID) == -1) {
                            lineIDs.push(updateStateParty[i].Line_ID);
                        }
                        if (updateParty.Net_Broker_Ind == 'Y') {
                            if (
                                updateStateParty[i].Party_ID == partyID ||
                                updateStateParty[i].Broker.Entity_ID === updateStateParty[partyID].Broker.Entity_ID
                            ) {
                                updateStateParty[i].Net_Broker_Ind = 'Y';
                            } else if (updateStateParty[i].Broker.Entity_ID !== updateStateParty[partyID].Broker.Entity_ID) {
                                updateStateParty[i].Net_Broker_Ind = 'N';
                            }
                        } else if (updateParty.Net_Broker_Ind == 'N') {
                            if (
                                updateStateParty[i].Party_ID == partyID ||
                                updateStateParty[i].Broker.Entity_ID === updateStateParty[partyID].Broker.Entity_ID
                            ) {
                                updateStateParty[i].Net_Broker_Ind = 'N';
                            }
                        }
                    }
                }
                sc.patchState({ party: updateStateParty });
            }
        }

        for (var i in lineIDs) {
            sc.dispatch([new UpdateLine({ Line_ID: lineIDs[i] })]);
        }

        if (addNew) {
            sc.dispatch(new DeleteParty(updateParty.Party_ID));

            delete updateParty.Party_ID;
            delete updateParty.Line_ID;
            delete updateParty.Edit_Status;
            delete updateParty.Agreement;

            sc.dispatch(new AddParty(currentParty.nodeType, updateParty));
        } else {
            if (lineChange) {
                sc.dispatch([
                    new RemoveContractPartyItem(lineID, partyID, currentParty.nodeType),
                    new UpdateLine({ Line_ID: lineID }),
                    new AddContractPartyItem(newLineID, partyID, currentParty.nodeType),
                    new UpdateLine({ Line_ID: newLineID })
                ]);
            } else {
                sc.dispatch([new UpdateLine({ Line_ID: lineID })]);
            }
        }
    }

    updateOtherUnsetCommRates(rateValue: string, commRateProperty: CommRateType, stateRecord: any) {
        if (Number(rateValue) != 0) {
            for (var i in CommRateType) {
                if (CommRateType[i] == commRateProperty) {
                    continue;
                }

                if (
                    !(
                        stateRecord[CommRateType[i]] &&
                        stateRecord[CommRateType[i]] != '' &&
                        Number(stateRecord[CommRateType[i]]) != 0
                    )
                ) {
                    stateRecord[CommRateType[i]] = rateValue;
                }
            }
        }
    }

    @Action(DeleteParty)
    deleteParty(sc: StateContext<PartyStateModel>, action: DeleteParty) {
        const state = sc.getState();
        let partyID = action.partyID;
        let party = cloneDeep(state.party);
        let nodeType = party[action.partyID].nodeType;
        let lineID = party[action.partyID].Line_ID;

        let principal = null;
        //When deleting Party_Record, set the Principal_Ind = "N"
        if (party[action.partyID].Principal_Ind) {
            principal = party[action.partyID].Principal_Ind; //Get original value of Principal_Ind before changing to "N"
            party[action.partyID].Principal_Ind = 'N';
        }

        this.helper.setItemDeleted(party, partyID);

        sc.patchState({ party: party });
        sc.dispatch([new UpdateLine({ Line_ID: lineID })]);

        if (action.partyID < 0) {
            sc.dispatch([new RemoveContractPartyItem(lineID, action.partyID.toString(), nodeType)]);
        }

        if (nodeType == PartyNodeType.Contract_Broker && principal == 'Y') {
            sc.dispatch([new SyncContractBroker({ Entity_ID: '', Entity_Name: '' })]);
        }
    }

    @Action(RestoreParty)
    restoreParty(sc: StateContext<PartyStateModel>, action: RestoreParty) {
        const state = sc.getState();
        let partyID = action.partyID;
        let party = { ...state.party }; //shallow copy brokerParty from state
        let lineID = party[action.partyID].Line_ID;
        this.helper.setItemRestored(party, partyID);

        sc.patchState({ party: party });
        sc.dispatch([new UpdateLine({ Line_ID: lineID })]);
    }

    @Action(CloneParty)
    copyParty(sc: StateContext<PartyStateModel>, action: CloneParty) {
        const state = sc.getState();
        let srcID = null;

        srcID = action.partyID;

        if (!srcID) {
            throw 'Source brokerParty not found in store.';
        }

        let newID = this.helper.getTempID();
        let newParty = cloneDeep(state.party[srcID]);
        let lineID = newParty.Line_ID;

        //If the record being cloned was Principal, set the clone's Principal_Ind = "N"
        if (newParty.Principal_Ind == 'Y') {
            newParty.Principal_Ind = 'N';
        }
        this.helper.setItemAdded(newParty);
        newParty.Party_ID = newID;

        sc.patchState({
            currentID: newID,
            party: { ...state.party, [newID]: newParty }
        });

        sc.dispatch([new AddContractPartyItem(lineID, newID, newParty.nodeType), new UpdateLine({ Line_ID: lineID })]);
    }

    @Action(AddParty)
    addParty(sc: StateContext<PartyStateModel>, action: AddParty) {
        const state = sc.getState();

        let newID = this.helper.getTempID();
        let lineID = null;
        let newParty = this.helper.createDefaultItem(ContractObjectDefaults, action.partyNodeType, action.party);
        this.helper.setItemAdded(newParty);
        newParty.Party_ID = newID;
        newParty.nodeType = action.partyNodeType;

        //default line_code, program, and state from contract
        let contract = this.store.selectSnapshot(ContractState.contract);
        if (!newParty.Line_Code) {
            newParty.Line_Code = contract.Line_Code;
        }
        if (!newParty.SubLine_Code) {
            newParty.Program = contract.Program;
        }
        if (!newParty.State) {
            newParty.State = contract.Contract_State;
        }

        if (newParty.nodeType == PartyNodeType.Contract_FinanceCo) {
            let primaryLine = this.store.selectSnapshot(LineState.primaryLine);
            lineID = primaryLine.Line_ID;
            newParty.Line_ID = lineID;
        } else {
            let lines = this.store.selectSnapshot(LineState.lines);
            for (var i in lines) {
                if (lines[i].Line_Code == newParty.Line_Code) {
                    lineID = lines[i].Line_ID;
                    newParty.Line_ID = lineID;
                    break;
                }
            }
        }
        //When adding a Party record, set Principal_Ind = "N" if that Party type already has a Principal.
        for (var i in state.party) {
            if (state.party[i].nodeType == newParty.nodeType && state.party[i].Principal_Ind == 'Y') {
                newParty.Principal_Ind = 'N';
                break;
            }
        }

        if (
            newParty.nodeType == PartyNodeType.Contract_Broker &&
            newParty.Principal_Ind == 'Y' &&
            newParty.Broker &&
            newParty.Broker.Entity_ID
        ) {
            sc.dispatch([
                new SyncContractBroker({ Entity_ID: newParty.Broker.Entity_ID, Entity_Name: newParty.Broker.Entity_Name })
            ]);
        }

        if (newParty.nodeType == PartyNodeType.Contract_Broker) {
            for (var i in state.party) {
                if (state.party[i].nodeType == PartyNodeType.Contract_Broker && state.party[i].Net_Broker_Ind == 'Y') {
                    newParty.Net_Broker_Ind = 'N';
                    break;
                }
            }
        }

        if (newParty.nodeType == PartyNodeType.Contract_Supplier) {
            let riskGroups = this.store.selectSnapshot<any>(RiskGroupState.riskGroups);
            if (riskGroups && riskGroups.length == 1) {
                for (var i in riskGroups) {
                    newParty.RiskGroup_Code = riskGroups[i].RiskGroup_Code;
                    break;
                }
            }
        }

        sc.patchState({
            currentID: newID,
            party: { ...state.party, [newID]: newParty }
        });

        sc.dispatch([new AddContractPartyItem(lineID, newID, action.partyNodeType), new UpdateLine({ Line_ID: lineID })]);
    }

    @Action(UpdatePartyAgreement)
    updatePartyAgreement(sc: StateContext<PartyStateModel>, action: UpdatePartyAgreement) {
        const state = sc.getState();

        let partyID = action.party.Party_ID;
        let updateParty: any = { ...action.party };
        let currentParty: any = { ...state.party[partyID] };
        let lineID = currentParty.Line_ID;
        this.helper.setItemChanged(updateParty, state.party[partyID].Edit_Status);

        if (action.agreementValues) {
            if (!updateParty.Agreement) {
                updateParty.Agreement = {
                    Agmt_ID: '',
                    Agmt_Term_ID: '',
                    Agmt_Num: ''
                };
            }
            updateParty.agreementValidated = 'Y';
            updateParty.Agreement.Agmt_Term_ID = action.agreementValues.Agmt_Term_ID;
            updateParty.Agreement.Agmt_ID = action.agreementValues.Agmt_ID;
            if (action.agreementValues.BrokerAgreement) {
                updateParty.Agreement.Agmt_Num = action.agreementValues.BrokerAgreement.Agmt_Num;
                updateParty.Broker.Entity_ID = action.agreementValues.BrokerAgreement.Broker.Entity_ID;
                updateParty.Broker.Entity_Name = action.agreementValues.BrokerAgreement.Broker.Entity_Name;
            }
            if (action.agreementValues.SupplierAgreement) {
                updateParty.Agreement.Agmt_Num = action.agreementValues.SupplierAgreement.Agmt_Num;
                updateParty.Source_Supplier.Entity_ID = action.agreementValues.SupplierAgreement.Supplier.Entity_ID;
                updateParty.Source_Supplier.Entity_Name = action.agreementValues.SupplierAgreement.Supplier.Entity_Name;
                if (
                    action.agreementValues.SupplierAgreement.Risk_Supplier &&
                    action.agreementValues.SupplierAgreement.Risk_Supplier.Entity_ID != ''
                ) {
                    updateParty.Risk_Supplier.Entity_ID = action.agreementValues.SupplierAgreement.Risk_Supplier.Entity_ID;
                    updateParty.Risk_Supplier.Entity_Name = action.agreementValues.SupplierAgreement.Risk_Supplier.Entity_Name;
                }
                if (
                    action.agreementValues.SupplierAgreement.Supplier.NAIC_Num &&
                    action.agreementValues.SupplierAgreement.Supplier.NAIC_Num != ''
                ) {
                    updateParty.Source_Supplier.NAIC_Num = action.agreementValues.SupplierAgreement.Supplier.NAIC_Num;
                }
            }
            updateParty.New_Comm_Rate = action.agreementValues.New_Comm_Rate;
            updateParty.Renewal_Comm_Rate = action.agreementValues.Renewal_Comm_Rate;
            updateParty.Rewrite_Comm_Rate = action.agreementValues.Rewrite_Comm_Rate;
            updateParty.Endorse_Comm_Rate = action.agreementValues.Endorse_Comm_Rate;
            updateParty.Renewal_Endorse_Comm_Rate = action.agreementValues.Renewal_Endorse_Comm_Rate;

            let rateType = this.store.selectSnapshot(ContractState.contract).Comm_Rate_Type;

            switch (rateType) {
                case 'RENEWAL':
                    updateParty.Comm_Rate = updateParty.Renewal_Comm_Rate;
                    break;
                case 'REWRITE':
                    updateParty.Comm_Rate = updateParty.Rewrite_Comm_Rate;
                    break;
                case 'ENDORSE':
                    updateParty.Comm_Rate = updateParty.Endorse_Comm_Rate;
                    break;
                case 'RENEWAL_ENDORSE':
                    updateParty.Comm_Rate = updateParty.Renewal_Endorse_Comm_Rate;
                    break;
                default:
                    updateParty.Comm_Rate = updateParty.New_Comm_Rate;
                    break;
            }
        }

        if (currentParty.nodeType == PartyNodeType.Contract_Broker && currentParty.Principal_Ind == 'Y') {
            if (updateParty.Broker && updateParty.Broker.Entity_ID != currentParty.Broker.Entity_ID) {
                sc.dispatch([new SyncContractBroker(updateParty.Broker)]);
            }
        }

        if (currentParty.nodeType == PartyNodeType.Contract_Broker) {
            if (updateParty.Broker && updateParty.Broker.Entity_ID != currentParty.Broker.Entity_ID) {
                updateParty.Affiliate = { Entity_ID: '', Entity_Name: '' };
                sc.dispatch([new SyncContractBrokerAffiliate(updateParty.Affiliate)]);
            }
        }

        if (currentParty.nodeType == PartyNodeType.Contract_Supplier && currentParty.Principal_Ind == 'Y') {
            if (
                !currentParty.Agreement ||
                (updateParty.Agreement &&
                    currentParty.Agreement &&
                    updateParty.Agreement.Agmt_Term_ID != currentParty.Agreement.Agmt_Term_ID)
            ) {
                if (action.agreementValues.Default_Team_Set_ID) {
                    this.teamSetService.initializeService();
                    this.teamSetService.getDefaultTeamSet(action.agreementValues.Default_Team_Set_ID);
                }

                // If Principal supplier's agreement term is set or changes, mark prin Broker node as changed.
                // Will trigger SDK to update with BK commision from SP agmt if set.
                // No actual changes are provided to the SDK.

                for (var b in state.party) {
                    let prinBroker: any = null;
                    if (
                        state.party[b].nodeType == PartyNodeType.Contract_Broker &&
                        state.party[b].Principal_Ind == 'Y' &&
                        state.party[b].Edit_Status != EditStatusType.Delete
                    ) {
                        prinBroker = state.party[b];
                        this.store.dispatch(new UpdateParty(prinBroker));
                        break;
                    }
                }
            }
        }

        sc.patchState({
            party: {
                ...state.party,
                [partyID]: {
                    ...state.party[partyID],
                    ...updateParty
                }
            }
        });
        sc.dispatch([new UpdateLine({ Line_ID: lineID })]);
    }

    @Action(ClearParty)
    clearParty(sc: StateContext<PartyStateModel>, action: ClearParty) {
        const state = sc.getState();

        let partyID = action.partyID;
        let updateParty: any = cloneDeep(state.party[partyID]);
        let lineID = updateParty.Line_ID;
        this.helper.setItemChanged(updateParty, state.party[partyID].Edit_Status);

        //Common between Broker and Supplier
        updateParty.Agreement = { Agmt_ID: '', Agmt_Num: '', Agmt_Term_ID: '' };
        updateParty.Comm_Amount = '';
        updateParty.Comm_Rate = '';
        updateParty.New_Comm_Rate = '';
        updateParty.Renewal_Comm_Rate = '';
        updateParty.Rewrite_Comm_Rate = '';
        updateParty.Endorse_Comm_Rate = '';
        updateParty.Renewal_Endorse_Comm_Rate = '';
        updateParty.Flat_Amount_Ind = 'N';
        updateParty.Manual_Comm_Ind = 'N';
        updateParty.Affiliate = { Entity_ID: '', Entity_Name: '' };

        //Broker Specific
        if (updateParty.Broker) {
            updateParty.Broker = { Entity_ID: '', Entity_Name: '' };
        }
        if (updateParty.Retail_Broker) {
            updateParty.Retail_Broker = { Entity_ID: '', Entity_Name: '' };
        }

        if (updateParty.nodeType == PartyNodeType.Contract_Broker && updateParty.Principal_Ind == 'Y' && action.sync) {
            sc.dispatch([new SyncContractBroker(updateParty.Broker), new SyncContractBrokerAffiliate(updateParty.Affiliate)]);
        }

        //Supplier Specific
        if (updateParty.Source_Supplier) {
            updateParty.Source_Supplier = { Entity_ID: '', Entity_Name: '' };
        }
        if (updateParty.Risk_Supplier) {
            updateParty.Risk_Supplier = { Entity_ID: '', Entity_Name: '' };
        }
        if (updateParty.Ref_Contract_Num) {
            updateParty.Ref_Contract_Num = '';
        }

        sc.patchState({
            party: {
                ...state.party,
                [partyID]: {
                    ...state.party[partyID],
                    ...updateParty
                }
            }
        });
        sc.dispatch([new UpdateLine({ Line_ID: lineID })]);
    }

    @Action(SetTPBillToEntity)
    setTPBillToEntity(sc: StateContext<PartyStateModel>, action: SetTPBillToEntity) {
        sc.patchState({
            tpBillToEntity: action.TPBillToEntity
        });
    }
}
